/**
 * User Engagement Tracking Service
 * Tracks user sessions and activity for dashboard analytics
 */

import { supabase } from './supabase';

let currentSession = null;
let sessionStartTime = null;
let gamesPlayed = 0;
let pagesVisited = new Set();

/**
 * Start a new user engagement session
 */
export const startEngagementSession = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return; // Don't track anonymous users
    
    // End any existing session first
    if (currentSession) {
      await endEngagementSession();
    }
    
    sessionStartTime = new Date();
    gamesPlayed = 0;
    pagesVisited = new Set();
    
    // Create new session record
    const { data, error } = await supabase
      .from('user_engagement_sessions')
      .insert([{
        user_id: user.id,
        session_start: sessionStartTime.toISOString(),
        games_played: 0,
        total_play_time_minutes: 0,
        pages_visited: [],
        device_type: getDeviceType()
      }])
      .select()
      .single();
    
    if (error) {
      console.error('Error starting engagement session:', error);
      return;
    }
    
    currentSession = data;
    console.log('Started engagement session:', currentSession.id);
    
  } catch (error) {
    console.error('Error in startEngagementSession:', error);
  }
};

/**
 * End the current engagement session
 */
export const endEngagementSession = async () => {
  try {
    if (!currentSession || !sessionStartTime) return;
    
    const sessionEnd = new Date();
    const totalMinutes = Math.round((sessionEnd - sessionStartTime) / (1000 * 60));
    
    const { error } = await supabase
      .from('user_engagement_sessions')
      .update({
        session_end: sessionEnd.toISOString(),
        games_played: gamesPlayed,
        total_play_time_minutes: totalMinutes,
        pages_visited: Array.from(pagesVisited)
      })
      .eq('id', currentSession.id);
    
    if (error) {
      console.error('Error ending engagement session:', error);
    } else {
      console.log('Ended engagement session:', currentSession.id, `${totalMinutes} minutes`);
    }
    
    currentSession = null;
    sessionStartTime = null;
    gamesPlayed = 0;
    pagesVisited.clear();
    
  } catch (error) {
    console.error('Error in endEngagementSession:', error);
  }
};

/**
 * Track page visit
 */
export const trackPageVisit = (pagePath) => {
  try {
    if (!currentSession) return;
    
    pagesVisited.add(pagePath);
    console.log('Tracked page visit:', pagePath);
    
  } catch (error) {
    console.error('Error tracking page visit:', error);
  }
};

/**
 * Track game play
 */
export const trackGamePlay = () => {
  try {
    if (!currentSession) return;
    
    gamesPlayed++;
    console.log('Tracked game play, total:', gamesPlayed);
    
  } catch (error) {
    console.error('Error tracking game play:', error);
  }
};

/**
 * Update session periodically (call this every few minutes)
 */
export const updateCurrentSession = async () => {
  try {
    if (!currentSession || !sessionStartTime) return;
    
    const now = new Date();
    const totalMinutes = Math.round((now - sessionStartTime) / (1000 * 60));
    
    const { error } = await supabase
      .from('user_engagement_sessions')
      .update({
        games_played: gamesPlayed,
        total_play_time_minutes: totalMinutes,
        pages_visited: Array.from(pagesVisited)
      })
      .eq('id', currentSession.id);
    
    if (error) {
      console.error('Error updating engagement session:', error);
    }
    
  } catch (error) {
    console.error('Error in updateCurrentSession:', error);
  }
};

/**
 * Get device type
 */
const getDeviceType = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (/mobile|android|iphone|ipad|phone/i.test(userAgent)) {
    return 'mobile';
  } else if (/tablet|ipad/i.test(userAgent)) {
    return 'tablet';
  } else {
    return 'desktop';
  }
};

/**
 * Initialize engagement tracking
 */
export const initializeEngagementTracking = () => {
  // Start session when user logs in
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_IN' && session?.user) {
      startEngagementSession();
    } else if (event === 'SIGNED_OUT') {
      endEngagementSession();
    }
  });
  
  // Update session every 2 minutes
  setInterval(updateCurrentSession, 2 * 60 * 1000);
  
  // End session when user leaves
  window.addEventListener('beforeunload', () => {
    if (currentSession) {
      // Use sendBeacon for reliable tracking on page unload
      navigator.sendBeacon('/api/end-session', JSON.stringify({
        sessionId: currentSession.id,
        gamesPlayed,
        totalMinutes: Math.round((new Date() - sessionStartTime) / (1000 * 60)),
        pagesVisited: Array.from(pagesVisited)
      }));
    }
  });
  
  // Track page changes for SPAs
  let lastPath = window.location.pathname;
  const observer = new MutationObserver(() => {
    if (window.location.pathname !== lastPath) {
      trackPageVisit(window.location.pathname);
      lastPath = window.location.pathname;
    }
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  // Initial page visit
  trackPageVisit(window.location.pathname);
};
